import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CalendarX, CreditCard, Receipt, Star } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { fileUrl, formatINR, formatApiError } from "@/lib/api";
import { payForBooking } from "@/lib/payment";
import { SkeletonRow } from "@/components/Loaders";
import { EmptyState } from "@/components/EmptyState";
import { ReviewDialog } from "@/components/ReviewDialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { BOOKING_STATUS_STYLE, fmtStatus } from "@/constants/status";

const StatusBadge = ({ status }) => (
  <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${BOOKING_STATUS_STYLE[status] || "bg-stone-100"}`}>{fmtStatus(status)}</span>
);

const BookingCard = ({ b, onOpen, onCancel, onPay, onReview }) => {
  const paid = b.payment_status === "paid";
  const awaiting = ["awaiting_payment", "payment_failed"].includes(b.status);
  const advance = b.advance_amount || 0;
  return (
    <div data-testid={`booking-card-${b.id}`} className="rounded-2xl border border-stone-200 bg-white p-4 transition-transform hover:-translate-y-0.5">
      <div className="flex cursor-pointer gap-3" onClick={() => onOpen(b.id)}>
        <img src={fileUrl(b.property_image)} alt={b.property_name} className="h-16 w-16 rounded-xl object-cover" />
        <div className="flex-1">
          <div className="flex items-start justify-between">
            <p className="font-heading font-semibold text-stone-900 line-clamp-1">{b.property_name}</p>
            <StatusBadge status={b.status} />
          </div>
          <p className="mt-0.5 font-mono text-xs text-stone-400">{b.booking_ref}</p>
          <p className="text-sm text-stone-500">{b.date} · {b.booking_type}</p>
          <div className="mt-1 flex items-center gap-2">
            <p className="font-heading font-bold text-stone-900">{formatINR(b.total_amount)}</p>
            {paid && <span className="rounded-full bg-green-100 px-2 py-0.5 text-[10px] font-semibold text-green-700">ADVANCE PAID</span>}
            {awaiting && <span className="rounded-full bg-orange-100 px-2 py-0.5 text-[10px] font-semibold text-orange-700">PAY {formatINR(advance)}</span>}
          </div>
        </div>
      </div>
      <div className="mt-3 flex flex-wrap gap-2">
        {awaiting && (
          <Button data-testid={`pay-${b.id}`} size="sm" onClick={() => onPay(b)} className="flex-1 gap-1 rounded-xl bg-primary hover:bg-green-900"><CreditCard size={15} /> {b.status === "payment_failed" ? "Retry Pay" : "Pay Advance"}</Button>
        )}
        {paid && (
          <Button data-testid={`invoice-${b.id}`} size="sm" variant="outline" onClick={() => onOpen(b.id, true)} className="flex-1 gap-1 rounded-xl"><Receipt size={15} /> Receipt</Button>
        )}
        {b.status === "completed" && !b.reviewed && (
          <Button data-testid={`review-${b.id}`} size="sm" variant="outline" onClick={() => onReview(b)} className="flex-1 gap-1 rounded-xl text-amber-600"><Star size={15} /> Review</Button>
        )}
        {["pending", "awaiting_payment", "confirmed", "payment_failed", "payment_expired"].includes(b.status) && (
          <Button data-testid={`cancel-${b.id}`} size="sm" variant="outline" className="flex-1 rounded-xl text-destructive hover:bg-red-50" onClick={() => onCancel(b.id)}>Cancel</Button>
        )}
      </div>
    </div>
  );
};

export default function MyBookings() {
  const navigate = useNavigate();
  const [bookings, setBookings] = useState(null);
  const [reviewBooking, setReviewBooking] = useState(null);

  const load = () => api.get("/bookings/my").then((r) => setBookings(r.data));
  useEffect(() => { load(); }, []);

  const cancel = async (bid) => {
    try {
      await api.patch(`/bookings/${bid}/cancel`);
      toast.success("Booking cancelled");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const pay = (b) => payForBooking(b.id, (data) => (data ? navigate(`/booking-confirmed/${b.id}`) : load()));
  const open = (bid, invoice) => navigate(invoice ? `/invoice/${bid}` : `/bookings/${bid}`);

  const groups = {
    upcoming: (bookings || []).filter((b) => ["pending", "awaiting_payment", "confirmed", "payment_failed", "payment_expired"].includes(b.status)),
    completed: (bookings || []).filter((b) => b.status === "completed"),
    cancelled: (bookings || []).filter((b) => ["cancelled", "rejected", "refund_pending", "refunded"].includes(b.status)),
  };

  return (
    <div className="px-4 pt-5">
      <h1 className="font-heading text-2xl font-semibold text-stone-900">My Bookings</h1>
      <Tabs defaultValue="upcoming" className="mt-4">
        <TabsList className="w-full rounded-full bg-stone-100">
          <TabsTrigger value="upcoming" data-testid="tab-upcoming" className="flex-1 rounded-full">Upcoming</TabsTrigger>
          <TabsTrigger value="completed" data-testid="tab-completed" className="flex-1 rounded-full">Completed</TabsTrigger>
          <TabsTrigger value="cancelled" data-testid="tab-cancelled" className="flex-1 rounded-full">Cancelled</TabsTrigger>
        </TabsList>
        {Object.entries(groups).map(([key, list]) => (
          <TabsContent key={key} value={key} className="mt-4 space-y-3">
            {bookings === null ? (
              [1, 2].map((i) => <SkeletonRow key={i} />)
            ) : list.length === 0 ? (
              <EmptyState icon={CalendarX} title="No bookings here" message="You have no bookings in this category yet. Start exploring properties to make your first booking." action={<Button className="rounded-full bg-primary hover:bg-green-900" onClick={() => navigate("/explore")}>Explore Properties</Button>} />
            ) : (
              list.map((b) => <BookingCard key={b.id} b={b} onOpen={open} onCancel={cancel} onPay={pay} onReview={setReviewBooking} />)
            )}
          </TabsContent>
        ))}
      </Tabs>
      {reviewBooking && (
        <ReviewDialog booking={reviewBooking} open={!!reviewBooking} onOpenChange={(v) => !v && setReviewBooking(null)} onDone={load} />
      )}
    </div>
  );
}
